import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { expenseService, expenseCategoryService, staffService, Expense, ExpenseCategory, Staff } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  IndianRupee,
  Plus,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Pencil,
  Bike,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';

export default function StaffExpensesPage() {
  const { session } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [staffProfile, setStaffProfile] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    category: '',
    clientName: '',
    remark: '',
    km: '',
    isGst: false,
    bank: 'HDFC',
    paymentMethod: 'GPay',
    rfNo: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!session?.userId) return;
    try {
      const [expData, catData, profileData, clientData] = await Promise.all([
        expenseService.getAll({ staffId: session.userId }),
        expenseCategoryService.getAll(),
        staffService.getById(session.userId),
        expenseService.getExternalClients(),
      ]);
      setExpenses(expData);
      setCategories(catData);
      setStaffProfile(profileData || null);
      setClients(clientData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const perKmRate = staffProfile?.bikeRatePerKm || 0;

  const handleKmChange = (val: string) => {
    const kmVal = val;
    if (kmVal && !isNaN(parseFloat(kmVal)) && parseFloat(kmVal) > 0) {
      const calc = perKmRate > 0 ? (parseFloat(kmVal) * perKmRate).toFixed(2) : '';
      setFormData(prev => ({
        ...prev,
        km: kmVal,
        amount: calc
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        km: kmVal,
        amount: ''
      }));
    }
  };

  const openCreateDialog = () => {
    setEditingExpense(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      amount: '',
      category: '',
      clientName: '',
      remark: '',
      km: '',
      isGst: false,
      bank: 'HDFC',
      paymentMethod: 'GPay',
      rfNo: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (exp: Expense) => {
    if (exp.status !== 'pending') return;
    setEditingExpense(exp);
    setFormData({
      date: exp.date,
      amount: exp.amount.toString(),
      category: exp.category || '',
      clientName: exp.clientName || '',
      remark: exp.remark || '',
      km: '',
      isGst: !!exp.isGst,
      bank: exp.bank || 'HDFC',
      paymentMethod: exp.paymentMethod || 'GPay',
      rfNo: exp.rfNo || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!session?.userId) return;
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    if (!formData.category) {
      toast({ title: 'Please select a category', variant: 'destructive' });
      return;
    }

    const isSpecialSelected = categories.find(c => c.name === formData.category)?.isSpecial === 1;
    if (isSpecialSelected && !formData.clientName.trim()) {
      toast({ title: 'Please select a Client Name', variant: 'destructive' });
      return;
    }

    let finalRemark = formData.remark;
    if (formData.category === 'Travel Allowance' && formData.km && parseFloat(formData.km) > 0) {
      const kmInfo = `${formData.km} KM @ ₹${perKmRate}/km`;
      if (!finalRemark.includes(kmInfo)) {
        finalRemark = finalRemark ? `${finalRemark} (${kmInfo})` : `Travel Allowance (${kmInfo})`;
      }
    }

    setSubmitting(true);
    try {
      if (editingExpense) {
        await expenseService.update(editingExpense.id, {
          amount: parseFloat(formData.amount),
          remark: finalRemark,
        });
        toast({ title: 'Expense updated' });
      } else {
        await expenseService.create({
          staffId: session.userId,
          staffName: session.name,
          date: formData.date,
          amount: parseFloat(formData.amount),
          remark: finalRemark,
          category: formData.category,
          clientName: isSpecialSelected ? formData.clientName.trim() : undefined,
          isGst: isSpecialSelected ? formData.isGst : undefined,
          bank: isSpecialSelected ? formData.bank : undefined,
          paymentMethod: isSpecialSelected ? formData.paymentMethod : undefined,
          rfNo: isSpecialSelected ? formData.rfNo : undefined,
        });
        toast({ title: 'Expense submitted for approval' });
      }
      setDialogOpen(false);
      setEditingExpense(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        category: '',
        clientName: '',
        remark: '',
        km: '',
        isGst: false,
        bank: 'HDFC',
        paymentMethod: 'GPay',
        rfNo: '',
      });
      await loadData();
    } catch (error) {
      toast({ title: 'Failed to save expense', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const approvedTotal = expenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);
  const pendingTotal = expenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0);
  const rejectedTotal = expenses.filter(e => e.status === 'rejected').reduce((sum, e) => sum + e.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <IndianRupee className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500 shrink-0" />
            My Expenses
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1">Submit company work expenses for admin approval</p>
        </div>
        <Button onClick={openCreateDialog} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Add Expense
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-white/50 dark:bg-slate-900/40 border-emerald-200/50 dark:border-emerald-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-emerald-500/10 shrink-0">
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-2xl font-black text-emerald-600 dark:text-emerald-400 truncate">₹{approvedTotal.toLocaleString('en-IN')}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-amber-200/50 dark:border-amber-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-amber-500/10 shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-2xl font-black text-amber-600 dark:text-amber-400 truncate">₹{pendingTotal.toLocaleString('en-IN')}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/50 dark:bg-slate-900/40 border-rose-200/50 dark:border-rose-800/30">
          <CardContent className="p-2.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-3 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-xl bg-rose-500/10 shrink-0">
              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600 dark:text-rose-400" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-2xl font-black text-rose-600 dark:text-rose-400 truncate">₹{rejectedTotal.toLocaleString('en-IN')}</p>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide truncate">Rejected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white/50 dark:bg-slate-900/40 overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          {expenses.length === 0 ? (
            <div className="text-center py-12">
              <IndianRupee className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">No expenses yet</p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Click "Add Expense" to submit your first expense</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Remark</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {new Date(exp.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        'px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide whitespace-nowrap',
                        exp.category === 'Travel Allowance'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                      )}>
                        {exp.category || 'Other Exp'}
                      </span>
                    </TableCell>
                    <TableCell className="font-bold">₹{exp.amount.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-slate-500 dark:text-slate-400">{exp.remark || '-'}</TableCell>
                    <TableCell>
                      <span className={cn(
                        'px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wide',
                        exp.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                        exp.status === 'pending' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                        'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'
                      )}>
                        {exp.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {exp.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(exp)}
                          className="h-7 px-2 text-[10px] font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        >
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update your pending expense' : 'Submit a company work expense for admin approval'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                disabled={!!editingExpense}
                className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Category *</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value, km: '' })}
                disabled={!!editingExpense}
                className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm"
              >
                <option value="">Select category...</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            {categories.find(c => c.name === formData.category)?.isSpecial === 1 && (
              <div className="p-4 rounded-2xl bg-indigo-50/50 dark:bg-slate-900/50 border border-indigo-100/50 dark:border-slate-805 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 tracking-wider uppercase">
                  Special Category Fields
                </p>

                {/* Client Selection */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Client Name *</Label>
                  <select
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    disabled={!!editingExpense}
                    className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm"
                  >
                    <option value="">Select Client...</option>
                    {clients.map((c, idx) => (
                      <option key={idx} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Payment Method */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Payment Method</Label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                      className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm"
                    >
                      <option value="GPay">GPay</option>
                      <option value="Online">Online</option>
                      <option value="NEFT">NEFT</option>
                      <option value="Cash">Cash</option>
                      <option value="Cheque">Cheque</option>
                    </select>
                  </div>

                  {/* Bank */}
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Bank Account</Label>
                    <select
                      value={formData.bank}
                      onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                      className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm"
                    >
                      <option value="HDFC">HDFC</option>
                      <option value="SBI">SBI</option>
                      <option value="ICICI">ICICI</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-1">
                  {/* GST Checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="gst-checkbox-page"
                      checked={formData.isGst}
                      onChange={(e) => setFormData({ ...formData, isGst: e.target.checked })}
                      className="w-4.5 h-4.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <Label htmlFor="gst-checkbox-page" className="text-xs font-semibold text-slate-600 dark:text-slate-400 cursor-pointer select-none">
                      Includes 18% GST
                    </Label>
                  </div>

                  {/* Reference Number */}
                  <div className="w-[50%] flex items-center gap-2 bg-white dark:bg-slate-900 rounded-xl px-2.5 py-1 border border-slate-200 dark:border-slate-800">
                    <Input
                      placeholder="Ref / Bill No."
                      value={formData.rfNo}
                      onChange={(e) => setFormData({ ...formData, rfNo: e.target.value })}
                      className="border-none bg-transparent focus-visible:ring-0 p-0 text-xs h-7"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.category === 'Travel Allowance' ? (
              <div className="p-3.5 rounded-2xl bg-purple-50/60 dark:bg-purple-950/20 border border-purple-200/50 dark:border-purple-800/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                    <Bike className="w-4 h-4 text-purple-600" />
                    Travel Allowance Options
                  </span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                    Rate: ₹{perKmRate}/km
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Kilometers (KM)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="e.g. 15"
                      value={formData.km}
                      onChange={(e) => handleKmChange(e.target.value)}
                      className="h-9 rounded-xl border-purple-200 dark:border-purple-900/50 bg-white dark:bg-slate-900"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-slate-600 dark:text-slate-400">Amount (₹)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      disabled={!!formData.km && parseFloat(formData.km) > 0}
                      className="h-9 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800"
                    />
                  </div>
                </div>

                {formData.km && parseFloat(formData.km) > 0 ? (
                  <p className="text-[11px] font-medium text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    ✓ {formData.km} KM × ₹{perKmRate}/km = ₹{formData.amount} (Amount section auto-calculated & disabled)
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    Fill KM to auto-calculate amount, or enter Amount directly for manual travel claim.
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Remark</Label>
              <Textarea
                placeholder="Expense details..."
                value={formData.remark}
                onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800"
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-slate-800/30 shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editingExpense ? 'Update' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
